import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';

const STATS = [
  { value: '2,400+', label: 'Active Partners' },
  { value: '98.6%', label: 'Compliance Rate' },
  { value: 'OMR 1.2B', label: 'Partner Revenue' },
];

const FEATURES = [
  { icon: 'ShieldCheck', text: 'End-to-end lifecycle management' },
  { icon: 'BarChart3', text: 'Real-time performance analytics' },
  { icon: 'Zap', text: 'AI-powered partner scoring' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeOrb, setActiveOrb] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveOrb((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(signInError?.message || 'Invalid email or password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (role) => {
    if (role === 'admin') {
      setEmail('admin@partnerhub.com');
      setPassword('Admin@123');
    } else {
      setEmail('manager@partnerhub.com');
      setPassword('Manager@123');
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen flex overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── LEFT PANEL ── */}
        <div className="hidden lg:flex lg:w-[55%] relative flex-col justify-between p-16 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1b3e 40%, #0a1628 70%, #060d1a 100%)' }}>

          {/* Animated mesh background */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Grid lines */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#4f8ef7" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            {/* Animated orbs */}
            <div className="absolute top-[15%] left-[10%] w-80 h-80 rounded-full opacity-20 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 0 ? 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' : 'radial-gradient(circle, #1d4ed8 0%, transparent 70%)', filter: 'blur(40px)' }} />
            <div className="absolute top-[50%] right-[5%] w-96 h-96 rounded-full opacity-15 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 1 ? 'radial-gradient(circle, #6366f1 0%, transparent 70%)' : 'radial-gradient(circle, #4338ca 0%, transparent 70%)', filter: 'blur(50px)' }} />
            <div className="absolute bottom-[10%] left-[20%] w-72 h-72 rounded-full opacity-20 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 2 ? 'radial-gradient(circle, #0ea5e9 0%, transparent 70%)' : 'radial-gradient(circle, #0284c7 0%, transparent 70%)', filter: 'blur(35px)' }} />

            {/* Floating particles */}
            {[...Array(12)]?.map((_, i) => (
              <div key={i}
                className="absolute w-1 h-1 rounded-full bg-blue-400/30"
                style={{
                  left: `${10 + (i * 7.5) % 80}%`,
                  top: `${15 + (i * 13) % 70}%`,
                  animation: `float ${3 + (i % 4)}s ease-in-out infinite alternate`,
                  animationDelay: `${i * 0.4}s`,
                }}
              />
            ))}
          </div>

          {/* Top: Logo */}
          <div className="relative z-10 flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
              <Icon name="Handshake" size={20} className="text-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">PartnerHub</span>
            <span className="ml-8 px-8 py-4 rounded-full text-[10px] font-semibold tracking-widest uppercase"
              style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' }}>
              Enterprise
            </span>
          </div>

          {/* Center: Hero content */}
          <div className="relative z-10 flex-1 flex flex-col justify-center py-16">
            {/* Eyebrow */}
            <div className="flex items-center gap-8 mb-24">
              <div className="w-8 h-px" style={{ background: 'linear-gradient(90deg, #3b82f6, transparent)' }} />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#60a5fa' }}>
                Partner Lifecycle Platform
              </span>
            </div>

            <h1 className="text-5xl font-bold leading-[1.1] mb-24 text-white" style={{ letterSpacing: '-0.02em' }}>
              Orchestrate your
              <br />
              <span style={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                partner ecosystem
              </span>
              <br />
              at scale.
            </h1>

            <p className="text-base leading-relaxed mb-48 max-w-sm" style={{ color: '#94a3b8' }}>
              From onboarding to offboarding — manage every stage of the partner lifecycle with precision, compliance, and AI-driven insights.
            </p>

            {/* Feature list */}
            <div className="space-y-16 mb-48">
              {FEATURES?.map((f, i) => (
                <div key={i} className="flex items-center gap-16">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <Icon name={f?.icon} size={14} className="text-blue-400" />
                  </div>
                  <span className="text-sm" style={{ color: '#cbd5e1' }}>{f?.text}</span>
                </div>
              ))}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-0">
              {STATS?.map((s, i) => (
                <React.Fragment key={i}>
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>{s?.value}</div>
                    <div className="text-xs mt-4" style={{ color: '#64748b' }}>{s?.label}</div>
                  </div>
                  {i < STATS?.length - 1 && (
                    <div className="w-px h-10 mx-16" style={{ background: 'rgba(255,255,255,0.08)' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Bottom: Testimonial card */}
          <div className="relative z-10 rounded-2xl p-24"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
            <div className="flex items-start gap-16">
              <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                SR
              </div>
              <div>
                <p className="text-sm leading-relaxed mb-12" style={{ color: '#94a3b8' }}>
                  "PartnerHub reduced our partner onboarding time by 60% and gave us complete visibility into compliance across 400+ partners."
                </p>
                <div className="flex items-center gap-8">
                  <span className="text-xs font-semibold text-white">Sarah Reynolds</span>
                  <span className="text-xs" style={{ color: '#475569' }}>· VP Partnerships, Nexora Inc.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex-1 flex flex-col justify-center items-center p-8 lg:p-16 relative overflow-hidden"
          style={{ background: '#f8fafc' }}>

          {/* Subtle background texture */}
          <div className="absolute inset-0 pointer-events-none opacity-40">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />
          </div>

          <div className="relative w-full max-w-[420px]">

            {/* Mobile logo */}
            <div className="flex lg:hidden items-center justify-center gap-12 mb-48">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                <Icon name="Handshake" size={20} className="text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">PartnerHub</span>
            </div>

            {/* Header */}
            <div className="mb-40">
              <h2 className="text-3xl font-bold text-slate-900 mb-8" style={{ letterSpacing: '-0.02em' }}>
                Welcome back
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                Sign in to your workspace to continue
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-12 p-16 rounded-xl mb-28 text-sm"
                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                <Icon name="AlertCircle" size={16} className="shrink-0" />
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-24">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-8" style={{ color: '#374151' }}>
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="Mail" size={16} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    placeholder="you@company.com"
                    className="w-full pl-48 pr-16 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-8">
                  <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
                    Password
                  </label>
                  <button type="button" className="text-xs font-medium transition-colors"
                    style={{ color: '#3b82f6' }}
                    onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
                    onMouseLeave={(e) => e.target.style.color = '#3b82f6'}>
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="Lock" size={16} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    required
                    placeholder="••••••••••"
                    className="w-full pl-48 pr-48 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-16 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#9ca3af' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#64748b'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-10 text-sm font-semibold text-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  height: '50px',
                  borderRadius: '12px',
                  background: loading ? '#3b82f6' : 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #6366f1 100%)',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35), 0 1px 3px rgba(0,0,0,0.1)',
                  transform: 'translateY(0)',
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; if (!loading) e.currentTarget.style.boxShadow = '0 8px 28px rgba(59,130,246,0.45), 0 2px 6px rgba(0,0,0,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35), 0 1px 3px rgba(0,0,0,0.1)'; }}>
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin"
                      style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={16} />
                    Sign in to workspace
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-16 my-32">
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
              <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>Quick access</span>
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
            </div>

            {/* Demo credentials */}
            <div className="grid grid-cols-2 gap-12">
              <button
                onClick={() => fillDemo('admin')}
                className="group relative overflow-hidden text-left transition-all duration-200"
                style={{
                  padding: '16px',
                  borderRadius: '14px',
                  background: '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.border = '1.5px solid #bfdbfe'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.border = '1.5px solid #e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div className="flex items-center gap-8 mb-8">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    <Icon name="Shield" size={12} className="text-white" />
                  </div>
                  <span className="text-xs font-bold" style={{ color: '#1e293b' }}>Admin</span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#94a3b8' }}>
                  admin@partnerhub.com
                  <br />
                  Admin@123
                </p>
              </button>

              <button
                onClick={() => fillDemo('manager')}
                className="group relative overflow-hidden text-left transition-all duration-200"
                style={{
                  padding: '16px',
                  borderRadius: '14px',
                  background: '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.border = '1.5px solid #bae6fd'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(14,165,233,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.border = '1.5px solid #e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div className="flex items-center gap-8 mb-8">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)' }}>
                    <Icon name="Users" size={12} className="text-white" />
                  </div>
                  <span className="text-xs font-bold" style={{ color: '#1e293b' }}>Manager</span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#94a3b8' }}>
                  manager@partnerhub.com
                  <br />
                  Manager@123
                </p>
              </button>
            </div>

            {/* Sign up link */}
            <div className="flex items-center gap-16 my-28">
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
              <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>New to PartnerHub?</span>
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
            </div>
            <Link
              to="/signup"
              className="w-full flex items-center justify-center gap-10 text-sm font-semibold transition-all duration-200"
              style={{
                height: '48px',
                borderRadius: '12px',
                background: '#ffffff',
                border: '1.5px solid #e2e8f0',
                color: '#374151',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.border = '1.5px solid #bfdbfe'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.border = '1.5px solid #e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
              <Icon name="UserPlus" size={16} />
              Create an account
            </Link>

            {/* Footer note */}
            <p className="text-center text-xs mt-28" style={{ color: '#94a3b8' }}>
              Protected by enterprise-grade security &amp; SOC 2 compliance
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) scale(1); opacity: 0.3; }
          100% { transform: translateY(-12px) scale(1.1); opacity: 0.6; }
        }
        input::placeholder { color: #9ca3af; }
      `}</style>
    </>
  );
};

export default LoginPage;
